import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, RotateCw } from "lucide-react";
import { api } from "../api/client";
import { useMutation } from "../api/resource";
import type { DossierDocument } from "../api/types";
import { PeriodCell, ProcessingCell, UsedBy, typeLabel } from "../components/documents";
import { EmptyState, ErrorState, PageHeader, SkeletonRows } from "../components/ui";
import { useDossier } from "../dossier";
import { formatBytes, w } from "../i18n";

const FILTERS: { id: string; match: (d: DossierDocument) => boolean }[] = [
  { id: "all", match: () => true },
  { id: "attention", match: d => d.status === "needs_review" || d.status === "failed" },
  { id: "qrt", match: d => d.document_type === "QRT" },
  { id: "reports", match: d => d.document_type === "SFCR" || d.document_type === "RSR" },
  { id: "actuarial", match: d => d.document_type === "ACTUARIAL_REPORT" },
  { id: "financial", match: d => d.document_type === "FINANCIAL_REPORT" },
  { id: "other", match: d => !["QRT", "SFCR", "RSR", "ACTUARIAL_REPORT", "FINANCIAL_REPORT"].includes(d.document_type || "") },
];

function RetryButton({ document }: { document: DossierDocument }) {
  const { refresh, toast } = useDossier();
  const reprocess = useMutation(api.reprocess);
  return <button className="ws-btn sm" disabled={reprocess.pending} title={reprocess.error?.message}
    onClick={async () => { if (await reprocess.run(document.id)) { refresh(); toast(w("docs.reprocess")); } }}>
    <RotateCw size={12} />{w("common.retry")}</button>;
}

export function DocumentsPage() {
  const { dossier, documents, openUpload, inspect } = useDossier();
  const [filter, setFilter] = useState("all");
  const items = documents.data || [];
  const counts = useMemo(() => Object.fromEntries(FILTERS.map(f => [f.id, items.filter(f.match).length])), [items]);
  const visible = items.filter(FILTERS.find(f => f.id === filter)!.match);
  const dossierMissing = documents.error?.status === 404 && String(documents.error.message).toLowerCase().includes("dossier");

  return <>
    <PageHeader title={w("docs.title")} lead={w("docs.lead")}
      actions={<button className="ws-btn primary" onClick={() => openUpload()}><Plus size={16} />{w("docs.add")}</button>} />

    {documents.error && <ErrorState title={w("docs.loadError")} error={documents.error} onRetry={documents.reload}
      note={dossierMissing ? w("docs.dossierMissingNote") : undefined} />}
    {dossierMissing && <div className="ws-alert-actions" style={{ marginTop: -12, marginBottom: 16 }}>
      <Link className="ws-btn sm primary" to="/">{w("docs.dossierMissingAction")}</Link>
    </div>}

    {!documents.error && (documents.loading || items.length > 0) && <>
      <div className="ws-tabs" role="tablist" aria-label={w("docs.col.type")}>{FILTERS.map(f =>
        <button key={f.id} role="tab" className="ws-tab" aria-selected={filter === f.id} onClick={() => setFilter(f.id)}>
          {w(`docs.filter.${f.id}`)}<span className="ws-tab-count">{counts[f.id] ?? ""}</span></button>)}</div>

      <div className="ws-card ws-table-wrap">
        <table className="ws-table">
          <thead><tr>
            <th scope="col">{w("docs.col.document")}</th><th scope="col">{w("docs.col.type")}</th>
            <th scope="col">{w("docs.col.period")}</th><th scope="col">{w("docs.col.processing")}</th>
            <th scope="col">{w("docs.col.usedBy")}</th><th scope="col"><span className="ws-sr-only">{w("docs.col.actions")}</span></th>
          </tr></thead>
          <tbody>
            {documents.loading && <SkeletonRows columns={["70%", 90, 60, 110, 80, 40]} />}
            {visible.map(document => <tr key={document.id}>
              <td style={{ maxWidth: 360 }}><button className="ws-doc-name" onClick={() => inspect(document.id)}>{document.display_name}</button>
                {document.current_version && <span className="ws-cell-sub">{document.current_version.file_format.toUpperCase()} · {formatBytes(document.current_version.size_bytes)}
                  {document.current_version.version_number > 1 ? ` · v${document.current_version.version_number}` : ""}</span>}</td>
              <td>{document.document_type ? typeLabel(document.document_type) : <span className="ws-faint">{w("common.notAvailable")}</span>}
                {document.metadata_source === "user" && <span className="ws-cell-sub">{w("docs.corrected")}</span>}</td>
              <td><PeriodCell document={document} /></td>
              <td style={{ maxWidth: 300 }}><ProcessingCell document={document} /></td>
              <td><UsedBy codes={document.used_by} dossierId={dossier.id} /></td>
              <td><div className="ws-row" style={{ justifyContent: "flex-end", flexWrap: "nowrap" }}>
                {document.status === "failed" && <RetryButton document={document} />}
                <button className="ws-btn sm ghost" onClick={() => inspect(document.id)}>{w("common.inspect")}</button>
              </div></td>
            </tr>)}
          </tbody>
        </table>
        {!documents.loading && items.length > 0 && !visible.length && <p className="ws-card-pad ws-muted" style={{ margin: 0 }}>{w("docs.emptyFilter")}</p>}
      </div>
    </>}

    {!documents.loading && !documents.error && !items.length && <div className="ws-card">
      <EmptyState title={w("docs.empty.title")} body={w("docs.empty.body")}
        action={<button className="ws-btn primary" onClick={() => openUpload()}><Plus size={16} />{w("docs.add")}</button>} /></div>}
  </>;
}
